/**
 * KWC · Lazy hydration for product-card colour pickers.
 * ---------------------------------------------------------------------------
 * The collection grid renders each card's variant picker inside an inert
 * <template> (see snippets/kwc-card-variants.liquid). Inert templates never
 * upgrade their custom elements, load their thumbnail images, or run layout
 * measurement — which is what made 25 pickers on one page block the main thread
 * for tens of seconds.
 *
 * This clones a card's picker into place only when it is about to enter the
 * viewport (or on first hover/focus), so the cost is paid a few cards at a time
 * as the customer scrolls, instead of all at once on load. Cards in the first
 * viewport hydrate immediately, so their pickers work right away.
 *
 * Vanilla, dependency-free. Safe if there are no lazy pickers on the page.
 */
(function () {
  var HYDRATED = 'kwcHydrated';

  function hydrate(host) {
    if (!host || host.dataset[HYDRATED]) return;
    var tpl = host.querySelector(':scope > template');
    if (!tpl) return;
    host.dataset[HYDRATED] = '1';
    host.appendChild(tpl.content.cloneNode(true));
    tpl.remove();
  }

  var io =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (entry.isIntersecting) {
                hydrate(entry.target);
                io.unobserve(entry.target);
              }
            });
          },
          { rootMargin: '400px 0px' } // hydrate before the card is visible
        )
      : null;
  var watched = new WeakSet();

  function init() {
    var hosts = document.querySelectorAll('[data-kwc-cvariants]:not([data-' + 'kwc-hydrated])');
    if (!hosts.length) return;

    // No IntersectionObserver -> hydrate everything (old browsers are rare and
    // correctness beats the optimisation there).
    if (!io) {
      hosts.forEach(hydrate);
      return;
    }

    hosts.forEach(function (host) {
      if (watched.has(host)) return;
      watched.add(host);
      io.observe(host);

      // Hover / focus intent hydrates immediately, so a fast pointer never beats
      // the observer to an interactive picker.
      var card = host.closest('.product-grid__card, .product-card, li, article') || host;
      var eager = function () {
        hydrate(host);
        io.unobserve(host);
        card.removeEventListener('pointerenter', eager);
        card.removeEventListener('focusin', eager);
      };
      card.addEventListener('pointerenter', eager, { once: true });
      card.addEventListener('focusin', eager, { once: true });
    });
  }

  // Cards added later — Load more (assets/kwc-collection.js) and grids that a
  // filter or sort re-renders — get the same treatment.
  function watchForNewCards() {
    if (!('MutationObserver' in window)) return;
    var pending = false;
    new MutationObserver(function () {
      if (pending) return;
      pending = true;
      requestAnimationFrame(function () {
        pending = false;
        init();
      });
    }).observe(document.querySelector('main') || document.body, { childList: true, subtree: true });
  }

  function start() {
    init();
    watchForNewCards();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
